/**
 * Community Enablers — Geo Community team-member roster.
 *
 * POLICY_TEAM_ROSTERS.md §4. Each community defines team-member slots
 * with a freeform display_title (Mayor / Town Manager / Council Member /
 * whatever fits). Each slot may be linked to a Life4Earth user account
 * (linked_user_guid) or carry external-person data via the manual_*
 * fields.
 *
 * Roster only — does NOT grant platform permissions. ACL flows through
 * V2 permission_grants (Audience & Permissions tab). Future auto-
 * computed permission badge can join linked_user_guid to active grants
 * to display "Admin" / "Owner" badges on roster cards.
 *
 * Migration 170 retired the hardcoded role_type catalog (Mayor, Rotary
 * President, etc.), the is_core flag, and the role_label override
 * column in favor of the single freeform display_title.
 */

import {
  fetchRoleSlots,
  roleSlotResponseToEnabler,
  saveAllRoleSlots,
  enablerToUpsertPayload,
} from "../../api/communityRoleSlots";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface CommunityEnabler {
  role_slot_guid: string;
  community_entity_guid: string;
  /** Freeform human label for this team-member position. */
  display_title: string;
  /** Display priority (lower = higher on page) */
  sort_order: number;
  /** Migration 171 — when true, hide from public Key Community Leaders
   *  display. Admin Settings tab still shows it. Default false. */
  is_hidden?: boolean;
  /** FK to users table — when populated, pull profile from user record */
  linked_user_guid?: string | null;
  /** Used when linked_user_guid is null */
  manual_name?: string;
  manual_title?: string;
  manual_photo_url?: string;
  manual_email?: string;
  manual_phone?: string;
  /** External profile URL (LinkedIn, personal site, etc.) */
  external_profile_url?: string;
  /** Admin-internal notes */
  notes?: string;
  /** DEV-1157 Phase 2d. Optional Tier-1 role_definitions catalog linkage.
   *  When set, the slot inherits the definition's role_rhythm_guid and
   *  Phase 1c's hook materializes instances on user link. */
  source_definition_guid?: string | null;
  created_at?: string;
  updated_at?: string;
}

/* ------------------------------------------------------------------ */
/*  In-memory store (mirrors communityConfigOverrides pattern)         */
/* ------------------------------------------------------------------ */

/** Map<community_entity_guid (UPPER), CommunityEnabler[]> */
const enablerStore = new Map<string, CommunityEnabler[]>();

/** Fired on every in-memory mutation. Settings-tab consumers use this
 *  to re-render on drag-reorder / inline edit / add / remove. Public-
 *  facing consumers should NOT listen to this — they'd see dirty
 *  local edits before the admin clicks Save. */
function notify(communityGuid: string): void {
  window.dispatchEvent(new CustomEvent("l4e_community_enablers_changed", { detail: { communityGuid } }));
}

/** Fired only after persistEnablersToApi successfully writes to the
 *  server. Public-facing consumers (e.g. the Alignment tab leader
 *  grid) listen to this to refresh after a save, without responding
 *  to in-memory mutations during admin editing. */
function notifyPersisted(communityGuid: string): void {
  window.dispatchEvent(new CustomEvent("l4e_community_enablers_persisted", { detail: { communityGuid } }));
}

/* ---- Public API ---- */

export function getEnablersForCommunity(communityGuid: string): CommunityEnabler[] {
  return enablerStore.get(communityGuid.toUpperCase()) || [];
}

export function setEnablersForCommunity(communityGuid: string, enablers: CommunityEnabler[]): void {
  enablerStore.set(communityGuid.toUpperCase(), enablers);
  notify(communityGuid);
}

export function upsertEnabler(communityGuid: string, enabler: CommunityEnabler): void {
  const key = communityGuid.toUpperCase();
  const existing = enablerStore.get(key) || [];
  const idx = existing.findIndex(
    (e) => e.role_slot_guid.toUpperCase() === enabler.role_slot_guid.toUpperCase()
  );
  if (idx >= 0) {
    existing[idx] = { ...enabler };
  } else {
    existing.push({ ...enabler });
  }
  enablerStore.set(key, existing);
  notify(communityGuid);
}

export function removeEnabler(communityGuid: string, roleSlotGuid: string): void {
  const key = communityGuid.toUpperCase();
  const existing = enablerStore.get(key) || [];
  enablerStore.set(
    key,
    existing.filter((e) => e.role_slot_guid.toUpperCase() !== roleSlotGuid.toUpperCase())
  );
  notify(communityGuid);
}

/* ------------------------------------------------------------------ */
/*  API hydration                                                       */
/* ------------------------------------------------------------------ */

/** Track which communities have been hydrated from the API to avoid re-fetching */
const hydratedFromApi = new Set<string>();

/**
 * Fetch role slots from the API and populate the in-memory store.
 * Safe to call multiple times — skips if already hydrated (unless force=true).
 *
 * No demo seed fallback (migration 170 retired the hardcoded role
 * catalog that the seed function depended on). On API failure the
 * store is left as-is; consumers see whatever was there before
 * (empty array if first load failed).
 */
export async function hydrateEnablersFromApi(
  communityGuid: string,
  force = false
): Promise<void> {
  const key = communityGuid.toUpperCase();
  if (!force && hydratedFromApi.has(key)) return;

  try {
    const response = await fetchRoleSlots(communityGuid);
    const enablers = response.map(roleSlotResponseToEnabler);
    enablerStore.set(key, enablers);
    hydratedFromApi.add(key);
    notify(communityGuid);
  } catch {
    // API unreachable — leave store as-is. Caller can retry by calling
    // hydrateEnablersFromApi(guid, true).
  }
}

/**
 * Persist current in-memory state to the API (bulk save).
 * Returns the refreshed list from the server.
 */
export async function persistEnablersToApi(communityGuid: string): Promise<void> {
  const key = communityGuid.toUpperCase();
  const current = enablerStore.get(key) || [];
  const payloads = current.map(enablerToUpsertPayload);

  const response = await saveAllRoleSlots(communityGuid, payloads);
  const refreshed = response.map(roleSlotResponseToEnabler);
  enablerStore.set(key, refreshed);
  hydratedFromApi.add(key);
  notify(communityGuid);
  // Public-facing surfaces (CommunityEnablers on the Alignment tab) listen
  // for this event specifically and refresh from server. Without this,
  // in-flight admin edits would visibly leak to the public view before
  // the admin saves. See the _changed vs _persisted distinction in the
  // event docstrings above.
  notifyPersisted(communityGuid);
}
